export type SessionGridRow = {
  id: string;
  name: string;
  info: string;
  date: Date;
  runCount?: number;
}
export interface TimeDBProps {
  Time: string;
  Cones: number;
  lapTimeSetAt: Date | null;
  RunId: string;
  Index: number;
  timeComment: string,
  created?: string;
  updated?: string;
}

export const CTM_Colors = {
  // Primary brand colors
  ctm_green: '#7AB973',
  ctm_green_dark: '#5a8456',
  ctm_green_light: '#a8d4a1',

  // Status colors
  success: '#4caf50',
  warning: '#ff9500',
  error: '#f44336',
  info: '#2196f3',

  // Neutral colors
  light_grey: '#D3D3D3',
  grey: '#909090',
  dark_grey: '#353535',
  surface: '#f5f5f5',

  // Chart colors (for multiple series)
  chart_primary: '#7AB973',
  chart_secondary: '#82ca9d',
  chart_tertiary: '#66bb6a',
  chart_accent: '#ff9500',
} as const

export interface IRunData {
  info: string;
  frontwing: 'On' | 'High Drag' | 'Low Drag' | 'Off';
  rearwing: 'On' | 'High Drag' | 'Low Drag' | 'Off';
  undertray: 'On' | 'Off';
  event:
  | 'autocross'
  | 'endurance'
  | 'half-skidpad clockwise'
  | 'half-skidpad counterclockwise'
  | 'acceleration'
  | 'skipad event';
  track_temperature: number;
  driver: string;
  ambient_temperature: number;
  ambient_humidity: number;
  ambient_pressure: number;
  sky_condition: 'clear' | 'cloudy' | 'very cloudy';
  precipitationType: 'none' | 'rainy' | 'snowy';
  precipitationAmount: string;
  software: string;
  software_params: string;
  tire_compound: 'R20' | 'LC0' | 'Wet';
  tire_pressure: number[];
  camber_IA: number[];
  tire_temperature: number[];
  toe: number[];
  springs: number[];
  damper_rebound: number[];
  damper_compression: number[];
  arb_setting: string;
  corner_weight: number[];
  runComment: string;
}

export interface IFeedbackData {
  comment: string;
  grip: number;
  limit_Balance: number;
  control_corner_entry: number;
  control_corner_apex: number;
  control_corner_exit: number;
  stability_entry: number;
  stability_apex: number;
  stability_exit: number;
  braking_stability: number;
  braking_controlability: number;
  throttle_stability: number;
  throttle_controlability: number;
}

// Default values for new run creation
// These values are safe defaults - zeros for measurements, empty for optional text fields
export const defaultFormData: IRunData = {
  info: '',
  frontwing: 'On',
  rearwing: 'On',
  undertray: 'On',
  event: 'autocross',
  track_temperature: 0,
  driver: '',
  ambient_temperature: 0,
  ambient_humidity: 0,
  ambient_pressure: 0,
  sky_condition: 'clear',
  precipitationType: 'rainy',
  precipitationAmount: '',
  software: '',
  software_params: '',
  tire_compound: 'R20',
  tire_pressure: [0, 0, 0, 0],
  camber_IA: [0, 0, 0, 0],
  tire_temperature: [0, 0, 0, 0],
  toe: [0, 0, 0, 0],
  springs: [0, 0, 0, 0],
  damper_rebound: [0, 0],
  damper_compression: [0, 0],
  arb_setting: '',
  corner_weight: [0, 0, 0, 0],
  runComment: ''
}

// Liste der Felder, die als json gespeichert werden sollen
export const jsonFields: Array<keyof IRunData> = [
  'tire_pressure',
  'camber_IA',
  'tire_temperature',
  'toe',
  'springs',
  'damper_rebound',
  'damper_compression',
  'corner_weight',
];

// list of all fields that should be exportet to csv
export const columnsCSV:string[] = [
  'info',
  'driver',
  'event',
  'track_temperature',
  'ambient_temperature',
  'ambient_humidity',
  'ambient_pressure',
  'sky_condition',
  'frontwing',
  'rearwing',
  'undertray',
  'tire_compound',
  'tire_pressure',
  'camber_IA',
  'tire_temperature',
  'toe',
  'springs',
  'damper_rebound',
  'damper_compression',
  'corner_weight',
  'arb_setting',
  'software',
  'software_params',
  'created',
  // driver feedback columns
  'grip',
  'limit_Balance',
  'control_corner_entry',
  'control_corner_apex',
  'control_corner_exit',
  'stability_entry',
  'stability_apex',
  'stability_exit',
  'braking_stability',
  'braking_controlability',
  'throttle_stability',
  'throttle_controlability',
  'comment'
]

// Validation helper functions
export const validateRunData = (data: IRunData): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Required fields
  const driverStr = String(data.driver || '').trim();
  if (!driverStr) {
    errors.push('Driver name is required');
  }

  // Driver name should not be only numbers
  if (driverStr && /^\d+$/.test(driverStr)) {
    errors.push('Driver name cannot be only numbers');
  }

  // Numeric range validations
  if (data.track_temperature < -50 || data.track_temperature > 100) {
    errors.push('Track temperature must be between -50°C and 100°C');
  }

  if (data.ambient_temperature < -50 || data.ambient_temperature > 60) {
    errors.push('Ambient temperature must be between -50°C and 60°C');
  }

  if (data.ambient_humidity < 0 || data.ambient_humidity > 100) {
    errors.push('Humidity must be between 0% and 100%');
  }

  // Tire pressure validation (typical range for race tires)
  data.tire_pressure.forEach((pressure, index) => {
    if (pressure < 0 || pressure > 5) {
      const positions = ['FL', 'FR', 'RL', 'RR'];
      errors.push(`Tire pressure ${positions[index]} must be between 0 and 5 bar`);
    }
  });

  // Tire temperature validation
  data.tire_temperature.forEach((temp, index) => {
    if (temp < 0 || temp > 150) {
      const positions = ['FL', 'FR', 'RL', 'RR'];
      errors.push(`Tire temperature ${positions[index]} must be between 0°C and 150°C`);
    }
  });

  // Camber angle validation (typical range -10 to +5 degrees)
  data.camber_IA.forEach((camber, index) => {
    if (camber < -15 || camber > 10) {
      const positions = ['FL', 'FR', 'RL', 'RR'];
      errors.push(`Camber angle ${positions[index]} must be between -15° and +10°`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateTimeFormat = (time: string): boolean => {
  // Format should be sss.SSS (e.g., 045.123 or 12.456)
  const timeRegex = /^\d{1,3}\.\d{3}$/;
  return timeRegex.test(time) || time === '' || time === '0.000';
};


